import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "antd/dist/reset.css";
import { GET_User_Details_User_Management, GET_User_Details_User_Management_For_ClanCrew } from "../../services/userAPI";
import { useLazyQuery, useQuery } from "@apollo/client";
import PaginationPrevious from "../../Images/paginationPreviuosIcon.png";
import PaginationNext from "../../Images/paginationNextIcon.png";
import { AgGridReact } from "ag-grid-react";
import ClanCrewAdvancedNotificationModal from "./clanCrewAdvancedNotificationModal";
import ClanCrewQuestionCampaignModal from "./clanCrewQuestionModal";
import ClanCrewQuestionCampaignAnalytics from "./clanCrewQuestionCampaignAnalytics";
import ClanCrewCreateCohortModal from "./clanCrewCreateCohortModal";
import PageBreadcrumb from "../../reusableField/PageBreadcrumb";
import CustomNoRowsOverlay from "../CustomNoRowsOverlay";

const ClanCrewUserManagement = ({ index }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const roleId = location.state?.roleId;
    const accountId = location.state?.accountId;
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(1000);
    const name = location.state?.name;
    const accountName = location.state?.accountName;
    const [showInitialLoading, setShowInitialLoading] = useState(true);
    const [isAdvancedNotifVisible, setIsAdvancedNotifVisible] = useState(false);
    const [isQuestionCampaignVisible, setIsQuestionCampaignVisible] = useState(false);
    const [isQuestionAnalyticsVisible, setIsQuestionAnalyticsVisible] = useState(false);
    const [isCreateCohortVisible, setIsCreateCohortVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [roleIds, setRoleIds] = useState(() => {
        return localStorage.getItem("roleId") || location.state?.roleId || null;
    });

    const [fetchUserList, { data: userListData, loading: userListLoading, error: userListError }] = useLazyQuery(
        GET_User_Details_User_Management_For_ClanCrew,
        {
            fetchPolicy: "cache-and-network",
            skip: !roleId,
        }
    );

    useEffect(() => {
        if (roleId) {
            fetchUserList({
                variables: {
                    roleId,
                    filterByName: searchTerm || undefined,
                    joiningDateFilter: undefined,
                },
            });
        }
    }, [roleId, searchTerm, fetchUserList]);

    const [accountIds, setAccountIds] = useState(() => {
        return localStorage.getItem("accountId") || location.state?.accountId || null;
    });

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [cursor, setCursor] = useState(null);
    const { loading, error, data, refetch } = useQuery(GET_User_Details_User_Management_For_ClanCrew, {
        variables: {
            roleId: roleId || "",
            pagingCriteria: {
                cursor: cursor,
                limit: pageSize,
                sortOrder: "asc",
                sortByField: "name"
            }
        },
        skip: !roleId,
        fetchPolicy: "network-only", // Ensures we always get fresh data
    });

    useEffect(() => {
        if (data && data.getUsersForAccount) {
            setShowInitialLoading(true);
            const timer = setTimeout(() => {
                setShowInitialLoading(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [data]);
    const pageMeta = data?.getUsersForAccount?.pageMeta;

    const totalCount = data?.getUsersForAccount?.pageMeta?.totalCount || 0;
    const totalPages = Math.ceil(totalCount / pageSize);
    const handlePrevious = () => {
        if (pageMeta?.hasPreviousPage) {
            setCursor(pageMeta.previousPage);
            setCurrentPage((prev) => Math.max(prev - 1, 1));
        }
    };

    const handleNextt = () => {
        if (pageMeta?.hasNextPage) {
            setCursor(pageMeta.nextPage);
            setCurrentPage((prev) => Math.min(prev + 1, totalPages));
        }
    };

    const tableData = useMemo(() => {
        return (
            data?.getUsersForAccount?.data?.map((user, index) => ({
                key: user.UserId,
                UserId: user.id || null,
                id: user.employeeId || "",
                name: user.name || "Unnamed User",
                employeeMailId: user.email || "",
                gender: user.gender || "",
                genderId: user.genderId || null,
                joiningDate: user.joiningDate || "",
                designation: user.designation || "",
                designationId: user.designationId || null,
                surname: user.surname || "",
                dateOfBirth: user.dateOfBirth || "",
                city: user.city || "",
                branch: user.branch || "",
                role: user.role || "",
                phoneNumber: user.phoneNumber || "",
                address: user.address || "",
                state: user.state || "",
                zone: user.zone || "",
                highestEducationQualification: user.highestEducationQualification || "",
                previousEmployer: user.previousEmployer || "",
                previousExperience: user.previousExperience ? Number(user.previousExperience) : 0,
                profileActivated: user.profileActivated || false,
                roleId: user.roleId || null,
                roleStartDate: user.roleStartDate || "",
                accountId: user.accountId || null,
                accountName: user.accountName || "",
                reportingDate: user.reportingDate || "",
                employeeId: user.employeeId || "",
                employeeIds: user.reporting?.employeeId || "",
                employeeName: user.reporting?.name || "",
                stateName: user.stateName || "",
                cityName: user.cityName || "",
                employeeType: user.employeeType || "",
                employeeTypeId: user.employeeTypeId || null,
                numberOfAssociates: user.numberOfAssociates || 0,
                numberOfManagers: user.numberOfManagers || 0,
                status: user.status || 0,
                userType: user.userType?.userType || "",
            })) || []
        );
    }, [data?.getUsersForAccount?.data]);

    const handleView = (userId) => {
        navigate(`/clan-crew-user-profile/${userId}`, {
            state: {
                name: name || "Unnamed",
                accountName: accountName || "Unnamed Account",
                roleId: roleId || null,
                accountId: accountId || null,
            },
        });
    };

    // Add to existing state
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    const [gridReady, setGridReady] = useState(false);


    // Column Definitions
    const columnDefs = useMemo(() => [
        {
            headerName: "Employee ID",
            headerTooltip: "Employee ID",
            field: "id",
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
        },
        {
            headerName: "Name",
            headerTooltip: "Name",
            field: "name",
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            cellRenderer: (params) => (
                <span
                    style={{ cursor: "pointer" }}
                    onClick={() => handleView(params.data.UserId)}
                >
                    {params.value}
                </span>
            ),
        },
        {
            headerName: "Email",
            headerTooltip: "Email",
            field: "employeeMailId", // Changed from "email" to "employeeMailId" to match tableData
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            valueGetter: (params) => params.data?.employeeMailId || "N/A", // Fallback if email is missing
        },
        { headerName: "Phone", headerTooltip: "Phone", field: "phoneNumber", sortable: true, filter: "agTextColumnFilter", floatingFilter: true, },
        { headerName: "Designation", headerTooltip: "Designation", field: "designation", sortable: true, filter: "agTextColumnFilter", floatingFilter: true, },
        {
            headerName: "Date of Joining",
            headerTooltip: "Date of Joining",
            field: "joiningDate",
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            valueFormatter: (params) => {
                if (!params.value) return "";
                const date = new Date(params.value);
                if (isNaN(date)) return params.value; // fallback if not a valid date
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            }
        },
        { headerName: "City", headerTooltip: "City", field: "cityName", sortable: true, filter: "agTextColumnFilter", floatingFilter: true, },
        { headerName: "Branch", headerTooltip: "Branch", field: "branch", sortable: true, filter: "agTextColumnFilter", floatingFilter: true, },
        {
            headerName: "User Type",
            headerTooltip: "User Type",
            field: "userType",
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            valueGetter: (params) => params.data?.userType || "",
        },
        {
            headerName: "App Active/Inactive",
            headerTooltip: "App Active/Inactive",
            field: "status",
            sortable: true,
            filter: "agTextColumnFilter",
            floatingFilter: true,
            valueGetter: (params) => {
                const profileActivated = params.data?.profileActivated;
                return profileActivated === true ? "Active" : "Inactive";
            },
        },
    ], [selectedUsers, tableData, gridApi]);

    // Default Column Definitions
    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 100,
        resizable: true,
        sortable: true,
        filter: "agTextColumnFilter",
        floatingFilter: true,
        cellStyle: { padding: "10px" },
        tooltipValueGetter: (params) => params.value,
    }), []);

    // Grid Ready Handler
    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        setGridReady(true);
    };

    const noRowsOverlayComponent = () => {
        return <CustomNoRowsOverlay message="No users found" />;
    };

    const onFilterChanged = (params) => {
        if (params?.api) {
            const displayedCount = params.api.getDisplayedRowCount();
            if (displayedCount === 0) {
                params.api.showNoRowsOverlay();
            } else {
                params.api.hideOverlay();
            }
        }
    };

    const onSelectionChanged = () => {
        if (gridApi) {
            const selectedRows = gridApi.getSelectedRows();
            setSelectedUsers(selectedRows.map(row => row.key));
        }
    };

    useEffect(() => {
        if (location.state?.roleId && location.state?.roleId !== roleIds) {
            setRoleIds(location.state.roleId);
            localStorage.setItem("roleId", location.state.roleId);
        }

        if (location.state?.accountId && location.state?.accountId !== accountIds) {
            setAccountIds(location.state.accountId);
            localStorage.setItem("accountId", location.state.accountId);
        }
    }, [location.state?.roleId, location.state?.accountId]);

    const handleBack = () => {
        navigate("/clan-crew-dashboard"); // This will navigate back to the previous page
    }

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    // const accountId = location.state?.accountId;
    // const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    // const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded; // Added syncNeeded to the destructuring

    const handleBackToViewRole = () => {
        // Use existing roleId and accountName from location.state, with fallbacks
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate("/role-management-view", {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded, // Added syncNeeded to the state
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution: mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate("/clan-crew-dashboard");
    };

    return (
        <div className="user-management-container">
            <PageBreadcrumb homePath="/clan-crew-dashboard" onBack={handleBackToViewRole} extraButtons={
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    {/* CREATE COHORT BUTTON */}
                    <button
                        style={{
                            backgroundColor: "transparent",
                            color: "#AD0101",
                            border: "1px solid #AD0101",
                            padding: "9px 19px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                        onClick={() => setIsCreateCohortVisible(true)}
                    >
                        Cohort
                    </button>

                    {/* TARGETED NOTIFICATION BUTTON */}
                    <button
                        className="send-targeted-btn"
                        style={{
                            backgroundColor: "transparent",
                            color: "#AD0101",
                            border: "1px solid #AD0101",
                            padding: "9px 19px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                        onClick={() => setIsAdvancedNotifVisible(true)}
                    >
                        Send Targeted Notification
                    </button>

                    {/* PUSH SENTIMENT QUESTIONS BUTTON */}
                    <button
                        style={{
                            backgroundColor: "transparent",
                            color: "#AD0101",
                            border: "1px solid #AD0101",
                            padding: "9px 19px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                        onClick={() => setIsQuestionCampaignVisible(true)}
                    >
                        Push Questions
                    </button>

                    {/* VIEW QUESTION ANALYTICS BUTTON */}
                    <button
                        style={{
                            backgroundColor: "transparent",
                            color: "#AD0101",
                            border: "1px solid #AD0101",
                            padding: "9px 19px",
                            borderRadius: "4px",
                            fontWeight: "600",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                        onClick={() => setIsQuestionAnalyticsVisible(true)}
                    >
                        Question Analytics
                    </button>
                </div>
            }>
                <span onClick={handleBack} style={{ cursor: 'pointer' }}>{accountName}</span>
                <span>&gt;</span>
                <span onClick={handleBackToViewRole} style={{ cursor: 'pointer' }}>{name}</span>
                <span>&gt;</span>
                <span style={{ fontWeight: 'bold' }}>User management</span>
            </PageBreadcrumb>

            <ClanCrewQuestionCampaignModal
                visible={isQuestionCampaignVisible}
                onCancel={() => setIsQuestionCampaignVisible(false)}
                accountId={accountId}
            />

            <ClanCrewQuestionCampaignAnalytics
                visible={isQuestionAnalyticsVisible}
                onCancel={() => setIsQuestionAnalyticsVisible(false)}
            />

            <ClanCrewAdvancedNotificationModal
                visible={isAdvancedNotifVisible}
                onCancel={() => setIsAdvancedNotifVisible(false)}
                users={userListData?.getUsersForAccount?.data || []}
                roleId={roleId}
                accountId={accountId}
            />

            <ClanCrewCreateCohortModal
                visible={isCreateCohortVisible}
                onCancel={() => setIsCreateCohortVisible(false)}
                accountId={accountId}
                onCohortCreated={(cohort) => {
                    console.log('Cohort created:', cohort);
                }}
            />

            <div className="tab-content">
                <div className="ag-grid-wrapper">
                    <AgGridReact
                        rowData={tableData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        serverSideStoreType="partial"
                        cacheBlockSize={pageSize}
                        maxBlocksInCache={10}
                        pagination={true}
                        paginationPageSize={pageSize}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        onSelectionChanged={onSelectionChanged}
                        onFilterChanged={onFilterChanged}
                        className="user-management-ag-grid"
                        suppressCellFocus={true}
                        domLayout="autoHeight"
                        animateRows={true}
                        groupDisplayType="multipleColumns"
                        suppressHorizontalScroll={true}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        tooltipShowDelay={0}
                    />
                </div>
                <div style={{ display: "flex", alignItems: "center", backgroundColor: "white" }}>
                    <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
                        <div className="footer" style={{ textAlign: "right", padding: "10px", fontFamily: "Inter" }}>
                            Total users: <span style={{ fontWeight: "700", fontSize: "13px" }}>{totalCount}</span>
                        </div>
                    </div>
                    <img
                        src={PaginationPrevious}
                        onClick={handlePrevious}
                        alt="Less Than"
                        style={{
                            height: "18px",
                            width: "18px",
                            opacity: !pageMeta?.hasPreviousPage ? 0.5 : 1,
                            cursor: pageMeta?.hasPreviousPage ? "pointer" : "not-allowed"
                        }}
                    />
                    <span style={{ margin: "0 10px", }}>
                        Page <span style={{ fontWeight: "bold" }}>{currentPage}</span> of <span style={{ fontWeight: "bold" }}>{totalPages}</span>
                    </span>
                    <img
                        src={PaginationNext}
                        onClick={handleNextt}
                        alt="Greater Than"
                        style={{
                            height: "18px",
                            width: "18px",
                            opacity: !pageMeta?.hasNextPage ? 0.5 : 1,
                            cursor: pageMeta?.hasNextPage ? "pointer" : "not-allowed"
                        }}
                    />
                </div>
            </div>
        </div >
    );
};

export default ClanCrewUserManagement;